import { ChangeEventHandler, FC, FormEventHandler, useContext, useState, useEffect, useCallback } from 'react'
import cx from 'classnames'
import { authentication } from '@microsoft/teams-js'

import { TeamsFxContext } from '../Context'
import { ApplicationFrame } from '../ApplicationFrame/ApplicationFrame'
import { identifierPrefix, scopes } from '../../constants'
import EnvironmentError from './EnvironmentError'

import './Tab.css'
import { AppDescription } from './AppDescription'

export const Tab: FC<object> = () => {
  const { themeString, teamsUserCredential } = useContext(TeamsFxContext)
  const [domain, setDomain] = useState<string>(localStorage.getItem('domain') ?? '')
  const [authToken, setAuthToken] = useState<string | null>(localStorage.getItem('auth_token'))
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false)
  const [isEnvSuspended, setIsEnvSuspended] = useState<boolean>(false)

  const fetchIsAwake = async (): Promise<boolean> => {
    try {
      const response = await fetch(`https://${domain}/wakeup`, { method: 'GET', mode: 'no-cors' })
      if (response.ok) {
        return false
      } else {
        if (response.status === 404) throw new Error('404')
        if (response.status === 500) throw new Error('500')

        throw new Error(`${response.status}`)
      }
    } catch (e) {
      console.log(e)
      return true
    }
  }

  const wakeup = async (loginCallback: () => any): Promise<void> => {
    const isAwake = await fetchIsAwake()

    if (isAwake) {
      setIsEnvSuspended(false)
      loginCallback()
      return
    } else {
      setIsEnvSuspended(true)
    }

    const interval = setInterval(async (): Promise<void> => {
      const isAwake = await fetchIsAwake()

      if (isAwake) {
        setIsEnvSuspended(false)
        loginCallback()
        clearInterval(interval)
      }
    }, 5000)
  }

  const handleDomainChange: ChangeEventHandler<HTMLInputElement> = (e): void => {
    setDomain(e.target.value)
  }

  const checkEnvironmentStatus = useCallback((): void => {
    fetch(`https://${domain}/wakeup`)
      .then(response => {
        if (response.status === 404) {
          setIsEnvSuspended(false)
          console.log('Environment is awake!')
        } else {
          setIsEnvSuspended(true)
          console.log('Environment is not awake yet. Retrying...')
        }
      })
      .catch(error => {
        console.error('Error occurred while checking environment status', error)
      })
  }, [domain])

  useEffect(() => {
    const interval = setTimeout(checkEnvironmentStatus, 5000)

    return () => {
      clearInterval(interval)
    }
  }, [checkEnvironmentStatus])

  const handleSubmit: FormEventHandler = (e): void => {
    e.preventDefault()

    setIsLoggingIn(true)
    void wakeup(login)
  }

  const login = async (): Promise<void> => {
    const redirectUri = `${window.location.origin}/authCallback`

    const loginUrl = new URL(`https://${domain}/oauth2/auth`)
    loginUrl.searchParams.append('redirect_uri', redirectUri)
    loginUrl.searchParams.append('response_type', 'code')
    loginUrl.searchParams.append('client_id', `${identifierPrefix}.${domain}`)
    loginUrl.searchParams.append('scope', scopes.join(' '))

    const authenticateUrl = `${window.location.origin}/auth-start?authorization-url=${btoa(loginUrl.toString())}`

    authentication.authenticate({
      url: authenticateUrl
    })
      .then((result) => {
        console.log('Login succeeded: ' + result)

        if (result !== null) {
          setAuthToken(result)
          localStorage.setItem('domain', domain)
          localStorage.setItem('auth_token', result)
        } else {
          throw new Error('No token found in the storage')
        }
      })
      .catch((e) => {
        console.log('Could not authenticate!!!', e)
      })
      .finally(() => {
        setIsLoggingIn(false)
      })
  }

  const handleLogout = (): void => {
    setAuthToken(null)
    localStorage.removeItem('auth_token')
    localStorage.removeItem('initialLocation')
  }

  return (
    <div
      className={cx('home', {
        light: themeString === 'default',
        dark: themeString === 'dark',
        contrast: themeString !== 'default' && themeString !== 'dark'
      })}
    >
      {authToken === null
        ? (
          <AppDescription
            domain={domain}
            handleDomainChange={handleDomainChange}
            handleSubmit={handleSubmit}
            isLoggingIn={isLoggingIn}
            teamsUserCredential={teamsUserCredential}
          />
          )
        : (isEnvSuspended
            ? <EnvironmentError />
            : (
              <ApplicationFrame domain={domain} timesheet onLogout={handleLogout} />
              ))}
    </div>
  )
}
